import Icon from "@components/Atoms/Icon";
import useWatch from "@core-ui/hooks/useWatch";
import { cn } from "@core-ui/utils/cn";
import { NodeViewContextableWrapper } from "@ext/markdown/core/element/NodeViewContextableWrapper";
import { useBlockToolbar } from "@ext/markdown/elements/article/edit/helpers/useBlockToolbar";
import BlockCommentView from "@ext/markdown/elements/comment/edit/components/View/BlockCommentView";
import NoteMenuActions from "@ext/markdown/elements/note/edit/components/NoteMenuActions";
import { NoteType, noteIcons } from "@ext/markdown/elements/note/render/component/Note";
import { NodeViewContent, type NodeViewProps } from "@tiptap/react";
import { type MouseEvent, type ReactElement, useRef, useState } from "react";

const EditNote = (props: NodeViewProps): ReactElement => {
	const { node, getPos, updateAttributes, editor } = props;
	const isEditable = editor.isEditable;
	const hoverElementRef = useRef<HTMLDivElement>(null);
	const type = (node.attrs.type as NoteType) || NoteType.note;
	const collapsed = !!node.attrs.collapsed;
	const [expanded, setExpanded] = useState(!collapsed);
	const clickable = !expanded || collapsed;

	useWatch(() => {
		setExpanded(!collapsed);
	}, [collapsed]);

	const toggleExpanded = (e: MouseEvent<HTMLElement>) => {
		e.preventDefault();
		setExpanded((prev) => !prev);
	};

	useBlockToolbar(
		props,
		isEditable && (
			<NoteMenuActions editor={editor} getPos={getPos} node={node} updateAttributes={updateAttributes} />
		),
		{ comment: true },
	);

	return (
		<NodeViewContextableWrapper props={props} ref={hoverElementRef}>
			<BlockCommentView className="rounded-l-none rounded-r-lg" commentId={node.attrs?.comment?.id}>
				<div
					className={cn(
						"admonition",
						"admonition-row",
						`admonition-${type}`,
						collapsed && !expanded && "admonition-collapsed",
					)}
					data-component="note"
				>
					<div className="admonition-heading" contentEditable={false} suppressContentEditableWarning={true}>
						<div
							className={cn("admonition-icon", clickable && "clickable")}
							onClick={clickable ? toggleExpanded : null}
						>
							<Icon
								code={clickable ? (expanded ? "chevron-down" : "chevron-right") : noteIcons[type]}
								strokeWidth="2"
							/>
						</div>
					</div>
					<NodeViewContent className="admonition-content" />
				</div>
			</BlockCommentView>
		</NodeViewContextableWrapper>
	);
};

export default EditNote;
